import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import SectionProcessBg from '../components/SectionProcessBg';
// import { IconGlobe } from '../components/icons/BrandIcons';
import { EASE, staggerContainer } from '../motion/variants';

const STATS = [
  { value: 18, suffix: '+', label: 'Years in polymer color & additives', accent: 'gold' },
  { value: 2400, suffix: '+', label: 'Masterbatch formulations', accent: 'green', format: true },
  { value: 6, suffix: '', label: 'SEA markets served from regional hub', accent: 'blue' },
];

const FEATURES = [
  {
    title: 'Drop-in let-down ratios',
    text: 'Validated on your resin and line speed — from trial to full production scale.',
    accent: 'gold',
  },
  {
    title: 'Regulatory dossiers',
    text: 'Food-contact, medical and agricultural use — documentation ships with every order.',
    accent: 'blue',
  },
  {
    title: 'ASEAN network visibility',
    text: 'Local stock and lead-time visibility across India, UAE and Southeast Asia hubs.',
    accent: 'green',
  },
];

// const PROMISE = [
//   { title: 'Precision', text: 'Tight ΔE & dispersion control', icon: IconTarget, accent: 'gold' },
//   { title: 'Sustainability', text: 'Recyclable-ready chemistries', icon: IconLeaf, accent: 'green' },
//   { title: 'Partnership', text: 'Embedded technical support', icon: IconShield, accent: 'blue' },
// ];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.88, rotateX: 14 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    rotateX: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: EASE },
  }),
};

function AnimatedCounter({ value, suffix, active, format = false }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return undefined;

    const duration = 1600;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setCount(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(tick);
    };

    const frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, value]);

  const display = format ? count.toLocaleString('en-US') : count;

  return (
    <>
      {display}
      {suffix}
    </>
  );
}

export default function WhySection() {
  const reduceMotion = useReducedMotion();
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, amount: 0.35 });

  return (
    <section id="why" className="sec sec-process-wrap why-v2" data-palette="blue">
      <SectionProcessBg variant="quality" seed={9} />
      <div className="why-v2__ambient" aria-hidden="true">
        <span className="why-v2__orb why-v2__orb--1" />
        <span className="why-v2__orb why-v2__orb--2" />
        <span className="why-v2__orb why-v2__orb--3" />
        <span className="why-v2__scan" />
      </div>

      <div className="sec-process-content why-v2__inner">
        <motion.div
          className="why-v2__hdr"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="kicker k-gold" variants={fadeUp}>
            Why Partners Choose Auralux
          </motion.div>
          <motion.h2 className="title lt" variants={fadeUp}>
            Engineered for
            <br />
            <em>converters</em>
          </motion.h2>
          <motion.p className="why-v2__lead" variants={fadeUp}>
            Precision color, regulatory confidence, and regional reach — built for converters who cannot afford batch
            variance.
          </motion.p>
        </motion.div>

        <div className="why-v2__stats" ref={statsRef}>
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="why-v2__stat"
              data-accent={stat.accent}
              custom={i}
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover={
                reduceMotion
                  ? undefined
                  : { y: -10, scale: 1.03, transition: { type: 'spring', stiffness: 300, damping: 20 } }
              }
            >
              <span className="why-v2__stat-ring" aria-hidden="true" />
              <span className="why-v2__stat-shine" aria-hidden="true" />
              <div className="why-v2__stat-v">
                {reduceMotion ? (
                  <>
                    {stat.value}
                    {stat.suffix}
                  </>
                ) : (
                  <AnimatedCounter
                    value={stat.value}
                    suffix={stat.suffix}
                    active={statsInView}
                    format={stat.format}
                  />
                )}
              </div>
              <p className="why-v2__stat-l">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="why-v2__features"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {FEATURES.map((f, i) => (
            <motion.article
              key={f.title}
              className="why-v2__feature"
              data-accent={f.accent}
              variants={fadeUp}
              whileHover={
                reduceMotion
                  ? undefined
                  : { x: 8, transition: { type: 'spring', stiffness: 280, damping: 22 } }
              }
            >
              <motion.span
                className="why-v2__feature-idx"
                animate={reduceMotion ? undefined : { rotate: [0, 8, 0] }}
                transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut' }}
              >
                0{i + 1}
              </motion.span>
              <div>
                <h4>{f.title}</h4>
                <p>{f.text}</p>
              </div>
              <span className="why-v2__feature-line" aria-hidden="true" />
            </motion.article>
          ))}
        </motion.div>

        {/*
        <div className="why-v2__lower why-v2__lower--quote-only">
          <motion.div
            className="why-v2__promise"
            initial={{ opacity: 0, x: 48 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.85, ease: EASE }}
          >
            <h3>Our promise</h3>
            <div className="why-v2__promise-list">
              {PROMISE.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.title}
                    className="why-v2__promise-item"
                    data-accent={p.accent}
                    initial={{ opacity: 0, x: 32 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.65, delay: i * 0.14, ease: EASE }}
                    whileHover={reduceMotion ? undefined : { scale: 1.02, x: 6 }}
                  >
                    <span className="why-v2__promise-icon">
                      <Icon size={20} />
                    </span>
                    <div>
                      <h4>{p.title}</h4>
                      <p>{p.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.blockquote
            className="why-v2__quote"
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          >
            <motion.span
              className="why-v2__quote-mark"
              aria-hidden="true"
              animate={reduceMotion ? undefined : { opacity: [0.35, 0.7, 0.35] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              &ldquo;
            </motion.span>
            <p>
              We work with Auralux because of their superior quality and quick, direct communication. Holcobatch stands
              out — color disperses quicker and more evenly, with consistent quality and no surprises batch to batch.
            </p>
            <footer>
              <strong>Regional Packaging Director</strong>
              <span>ASEAN Flexible Packaging Converter</span>
              <span className="why-v2__quote-trusted">
                <IconGlobe size={14} aria-hidden />
                Trusted by converters across ASEAN &amp; Middle East
              </span>
            </footer>
          </motion.blockquote>
        </div>
        */}
      </div>
    </section>
  );
}
